import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  styled,
  Typography,
} from "@mui/material";
import { palette } from "../../themes/theme";
import ClearIcon from "@mui/icons-material/Clear";
import RadarIcon from "@mui/icons-material/Radar";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { useDispatch, useSelector } from "react-redux";
import {
  removeTradeArea,
  setActiveMarker,
  setShowAgsReport,
  toggleTradeArea,
} from "../../redux/slices/mapSlice";

const CustomIconButton = styled(IconButton)(() => ({
  height: "60%",
  background: palette.background.light,
  borderRadius: "4px",
  border: `1px solid ${palette.primary.main}`,
  padding: "3px",
}));

const LocationsAndTradeAreas = ({
  markers,
  setMarkers,
  markerRef,
  mapRef,
  resetAllOnceChanged,
}) => {
  const tradeAreasList = useSelector((state) => state.map.tradeAreas);

  const dispatch = useDispatch();

  // flyTo handler: safe extraction of coordinates from tradeArea and mapRef.flyTo
  const handleFlyTo = (tradeArea) => {
    if (!mapRef || !mapRef.flyTo) return;

    const coords =
      tradeArea?.latlng ??
      tradeArea?.coordinates ??
      tradeArea?.location ??
      null;

    let lng, lat;
    if (!coords) return;
    if (Array.isArray(coords) && coords.length >= 2) {
      lng = coords[0];
      lat = coords[1];
    } else if (
      coords.longitude !== undefined &&
      coords.latitude !== undefined
    ) {
      lng = coords.longitude;
      lat = coords.latitude;
    } else if (coords.lng !== undefined && coords.lat !== undefined) {
      lng = coords.lng;
      lat = coords.lat;
    } else if (coords.latlng) {
      lng = coords.latlng.lng ?? coords.latlng.longitude;
      lat = coords.latlng.lat ?? coords.latlng.latitude;
    } else {
      return;
    }

    if (typeof lng !== "number" || typeof lat !== "number") return;

    mapRef.flyTo({
      center: [lng, lat],
      zoom: 14,
    });
  };

  const toggleMaker = (tradeArea) => {
    const selectedMarker = markers.find((d) => d.id === tradeArea.id);

    selectedMarker.marker.getElement().style.display = tradeArea.selected
      ? "none"
      : "";

    markerRef.current = markers.map((d) =>
      d.id === tradeArea.id ? selectedMarker : d
    );
    setMarkers(markerRef.current);

    dispatch(toggleTradeArea(tradeArea.id));

    tradeArea.radiusSetting.forEach((setting, index) => {
      const circleSourceName = `circleData-${index}-${tradeArea.latlng.lng}-${tradeArea.latlng.lat}`;
      const circleLayer = mapRef.getLayer(circleSourceName);
      if (circleLayer) {
        const selectedTb = markers.find((d) => d.id === circleSourceName);
        if (selectedTb)
          selectedTb.marker.getElement().style.display = tradeArea.selected
            ? "none"
            : "";

        mapRef.setLayoutProperty(
          circleSourceName,
          "visibility",
          !tradeArea.selected ? "visible" : "none"
        );
        mapRef.setLayoutProperty(
          circleSourceName + "-line",
          "visibility",
          !tradeArea.selected ? "visible" : "none"
        );
      }
    });
  };

  const showMarkerSetting = (id) => {
    dispatch(setActiveMarker(id));
  };

  const showAgsReportSetting = (id) => {
    resetAllOnceChanged();
    dispatch(setShowAgsReport(id));
  };

  const handleDelete = (tradeArea) => {
    const selectedMarker = markers.find((d) => d.id === tradeArea.id);

    selectedMarker.marker.getElement().remove();

    markerRef.current = markers.filter((d) => d.id !== tradeArea.id);
    setMarkers(markerRef.current);
    dispatch(removeTradeArea(tradeArea.id));
    tradeArea.radiusSetting.forEach((_, index) => {
      const circleSourceName = `circleData-${index}-${tradeArea.latlng.lng}-${tradeArea.latlng.lat}`;
      const circleLayer = mapRef.getLayer(circleSourceName);
      if (circleLayer) {
        setMarkers((previousValues) => {
          const updatedMarkersList = previousValues.filter(
            (item) => item.id !== circleSourceName
          );
          const markerToBeRemoved = previousValues.filter(
            (item) => item.id === circleSourceName
          );
          if (
            markerToBeRemoved &&
            markerToBeRemoved.length &&
            markerToBeRemoved[0].marker
          ) {
            markerToBeRemoved[0].marker.remove();
          }
          return updatedMarkersList;
        });
        mapRef.removeLayer(circleSourceName + "-line");
        mapRef.removeLayer(circleSourceName);
        mapRef.removeSource(circleSourceName);
      }
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "1vh",
        justifyContent: "center",
      }}
    >
      {!tradeAreasList?.length ? (
        <Typography variant="body2" color="#00000080">
          No locations & trade areas found
        </Typography>
      ) : (
        tradeAreasList.map((tradeArea) => (
          <Box
            key={tradeArea.id}
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "space-between",
              alignItems: "center",
              background: palette.background.light,
              px: "0.5vw",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  onChange={() => toggleMaker(tradeArea)}
                  checked={tradeArea.selected}
                />
              }
              sx={{
                maxWidth: "8vw",
                "& .MuiFormControlLabel-label": {
                  maxWidth: "inherit",
                },
              }}
              label={
                <p
                  style={{
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    fontSize: "1.6vh",
                    // width: "25%",
                    margin: 0,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleFlyTo(tradeArea);
                  }}
                >
                  {tradeArea.name}
                </p>
              }
            />
            <Box sx={{ display: "flex", gap: 1 }}>
              {/* <CustomIconButton>
                <RadarIcon fontSize="medium" color="disabled" />
              </CustomIconButton> */}
              <CustomIconButton onClick={() => showMarkerSetting(tradeArea.id)}>
                <RadarIcon fontSize="medium" color="primary" />
              </CustomIconButton>
              <CustomIconButton
                disabled={
                  !(
                    (tradeArea.type === "polygon-trade-area" ||
                      tradeArea.radiusEnable) &&
                    tradeArea.selected
                  )
                }
                onClick={() => showAgsReportSetting(tradeArea.id)}
              >
                <DescriptionOutlinedIcon
                  fontSize="medium"
                  color={
                    !(
                      (tradeArea.type === "polygon-trade-area" ||
                        tradeArea.radiusEnable) &&
                      tradeArea.selected
                    )
                      ? "disabled"
                      : "primary"
                  }
                />
              </CustomIconButton>
              <CustomIconButton onClick={() => handleDelete(tradeArea)}>
                <ClearIcon fontSize="medium" color="primary" />
              </CustomIconButton>
            </Box>
          </Box>
        ))
      )}
    </Box>
  );
};

export default LocationsAndTradeAreas;
